// ============================================================
// attack/shotNotes - the lines a SHOT prints about its weapon.
//
// Pure: every function returns the lines, in the order the log prints
// them, and the caller pushes them. The wording is asserted verbatim by
// the unit and E2E suites - never reword a line here.
//
// `hasWS` / `getWSP` come through the engine BARREL, as they did when
// these lines lived in the hook: the hook tests mock the barrel, and a
// leaf import would slip past their fakes (gotchas.md, "The hook tests
// mock the engine BARREL").
// ============================================================
import { hasWS, getWSP } from '../../engine/index';
import { meltaNote, scatterNote, maximalNote } from '../../engine/attackCalc';
import { cyclingRounds } from '../../engine/fireModes';
import type { ResolvedArea } from '../../engine/areaShapes';

/** What a special's reminder may depend on besides the specials themselves. */
export interface ShotSituation {
  pen: number;
  rangeBand: string;
  isMelee: boolean;
  indirectNoLoS: boolean;
  psyRating: number;
}

type NoteRule = (sp: string[], s: ShotSituation) => string | null;

/** A reminder for a special that carries its number in brackets, or null when it is absent. */
const paramNote = (sp: string[], id: string, text: (x: number) => string): string | null => {
  const x = getWSP(sp, id);
  return x > 0 ? text(x) : null;
};

// Declarative: one row per special, in the order the log has always printed them.
const WEAPON_NOTES: NoteRule[] = [
  // These notes sit in the same list as ones describing what the attack DID
  // ("Razor Sharp: Pen doubled to 12"), so a reminder phrased like a claim
  // reads as one. The old "double Pen at Short Range (GM adjusts)" was
  // reported as Melta firing at Normal range; the doubling is gated in
  // damageCalc.
  (sp, s) => (hasWS(sp, 'Melta') ? meltaNote(s.pen, s.rangeBand) : null),
  (sp, s) => (hasWS(sp, 'Scatter') ? scatterNote(s.rangeBand) : null),
  (sp) => (hasWS(sp, 'Maximal') ? maximalNote() : null),
  (sp) =>
    hasWS(sp, 'Graviton')
      ? 'Graviton: damage = target AP at hit location' +
        (getWSP(sp, 'Graviton') > 1 ? ' × ' + getWSP(sp, 'Graviton') : '')
      : null,
  (sp) =>
    hasWS(sp, 'Devastating')
      ? paramNote(sp, 'Devastating', (x) => 'Devastating(' + x + '): +' + x + ' Magnitude vs hordes')
      : null,
  (sp, s) =>
    (hasWS(sp, 'Recharge') || hasWS(sp, 'Cycling')) && !s.isMelee
      ? 'Cycling: the capacitor drains - the weapon needs ' + cyclingRounds(sp) + ' round(s) to recharge'
      : null,
  (sp, s) =>
    s.indirectNoLoS && hasWS(sp, 'Indirect') && !s.isMelee
      ? 'Indirect: firing in an arc without line of sight (−10 BS)'
      : null,
  (sp) => (hasWS(sp, 'Flexible') ? 'Flexible: cannot be parried' : null),
  (sp) => (hasWS(sp, 'Unerring') ? 'Unerring: cannot be dodged' : null),
  (sp) => (hasWS(sp, 'Ethereal') ? 'Ethereal: only a power field or force weapon can parry it' : null),
  (sp) => paramNote(sp, 'Feinting', (x) => 'Feinting(' + x + '): reactions at -' + x),
  (sp) => paramNote(sp, 'Telegraphed', (x) => 'Telegraphed(' + x + '): reactions at +' + x),
  (sp) => (hasWS(sp, 'Cleaving') ? 'Cleaving: a successful parry still lets half damage through' : null),
  (sp) => (hasWS(sp, 'Seeking') ? 'Seeking: the round steers itself - cover and line of sight mean nothing' : null),
];

const RANGE_MODS: Record<string, number> = { pointBlank: 30, short: 10, normal: 0, long: -10, extreme: -30 };

/** The range band's line - ranged attacks off the Normal band only. */
function rangeNote(s: ShotSituation): string | null {
  if (s.isMelee || s.rangeBand === 'normal') return null;
  const mod = RANGE_MODS[s.rangeBand] || 0;
  return 'Range: ' + s.rangeBand.replace('pointBlank', 'Point Blank') + ' (' + (mod >= 0 ? '+' : '') + mod + ')';
}

// Declarative: the lines printed AFTER the template's sizes, in log order.
const HANDLING_NOTES: NoteRule[] = [
  (sp, s) => (hasWS(sp, 'Unwieldy') && s.isMelee ? 'Unwieldy: no Lightning Attack, cannot Parry' : null),
  (sp, s) => (hasWS(sp, 'Unbalanced') && s.isMelee ? 'Unbalanced: no Lightning Attack, -10 Parry' : null),
  (sp, s) =>
    hasWS(sp, 'Force') && s.psyRating > 0
      ? 'Force: +' + s.psyRating + ' damage (Psy Rating ' + s.psyRating + ')'
      : null,
  (_sp, s) => rangeNote(s),
];

/** The weapon's own reminders, printed before the template is rolled. */
export function weaponNotes(sp: string[], s: ShotSituation): string[] {
  return WEAPON_NOTES.map((rule) => rule(sp, s)).filter((n): n is string => n !== null);
}

/** Handling and range lines, printed after the template's sizes. */
export function handlingNotes(sp: string[], s: ShotSituation): string[] {
  return HANDLING_NOTES.map((rule) => rule(sp, s)).filter((n): n is string => n !== null);
}

const FALLOFF_TEXT: Record<string, (f: ResolvedArea['falloff']) => string> = {
  percent: (f) => f.value + '% per leap',
  flat: (f) => '−' + f.value + ' per leap',
  fixed: (f) => f.value + '% for every echo',
};

/** Does this template leap at all - an arc with somewhere to go and a leap to take? */
export function isLeapingArc(area: ResolvedArea): boolean {
  return area.shape === 'arc' && area.reach > 0 && area.leaps > 0;
}

/** "Arc: up to 2 leaps of 5m, 50% per leap" - null for anything that does not leap. */
export function arcNote(area: ResolvedArea): string | null {
  if (!isLeapingArc(area)) return null;
  const f = area.falloff;
  const falloffText = (FALLOFF_TEXT[f.mode] ?? ((d) => 'echoes roll ' + d.dice))(f);
  const leaps = area.leaps + ' leap' + (area.leaps === 1 ? '' : 's');
  return 'Arc: up to ' + leaps + ' of ' + area.reach + 'm, ' + falloffText;
}
